import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const { Text } = Typography;

const PmvFuelRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string | undefined>();
  const limit = 50;
  const query = useQuery({
    queryKey: ['pmv-fuel-register', q, page, status],
    queryFn: () =>
      pmvService.getFuelRegister({
        q: q || undefined,
        operational_status: status,
        limit,
        offset: (page - 1) * limit,
      }),
    staleTime: 20_000,
  });

  const rows = query.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Fleet Fuel Register"
        subtitle="Operational fuel attribution — server-paginated; no browser-side full scan"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/asset/pmv/fuel/control-tower')}>
              Fuel Control Tower
            </Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/attention')}>Attention</Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/reconciliation')}>
              Reconciliation
            </Button>
            <Button onClick={() => navigate('/asset/pmv/fuel/sources')}>Sources</Button>
          </Space>
        }
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Fleet number, asset code, or name"
              onSearch={(v) => {
                setPage(1);
                setQ(v);
              }}
              style={{ width: 280 }}
            />
            <Select
              allowClear
              placeholder="Ops status"
              style={{ width: 160 }}
              value={status}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              options={['AVAILABLE', 'ALLOCATED', 'IN_TRANSIT', 'BREAKDOWN', 'MAINTENANCE'].map(
                (v) => ({ value: v, label: v })
              )}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Consumption rates show Unavailable when meters or fuel evidence are insufficient — never fabricated zeros."
      />
      {!query.isLoading && rows.length === 0 ? (
        <Empty description="No equipment matches these fuel register filters" />
      ) : (
        <Table
          rowKey="pmv_profile_id"
          size="small"
          loading={query.isLoading}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: limit,
            total: query.data?.total ?? 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Equipment',
              key: 'equipment',
              width: 240,
              ellipsis: true,
              render: (
                _: unknown,
                r: {
                  pmv_profile_id: string;
                  equipment_label?: string;
                  fleet_number?: string | null;
                  asset_code?: string | null;
                  asset_name?: string | null;
                }
              ) => {
                const label =
                  r.equipment_label ||
                  r.fleet_number ||
                  (r.asset_code && r.asset_name ? `${r.asset_code} · ${r.asset_name}` : null) ||
                  r.asset_code ||
                  r.asset_name ||
                  'Equipment';
                return (
                  <div style={hireTableCellEllipsis}>
                    <Button
                      type="link"
                      style={hireTableLinkBtnStyle}
                      onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}?tab=fuel`)}
                    >
                      {label}
                    </Button>
                  </div>
                );
              },
            },
            {
              title: 'Events (ACTIVE)',
              dataIndex: 'attribution_count',
              width: 120,
            },
            {
              title: 'Litres',
              dataIndex: 'attributed_litres',
              width: 100,
              render: (v) =>
                v == null ? <Text type="secondary">Unavailable</Text> : Number(v).toFixed(1),
            },
            {
              title: 'Open attention',
              dataIndex: 'open_attention',
              width: 120,
              render: (v) => (v > 0 ? <Tag color="orange">{v}</Tag> : (v ?? 0)),
            },
            {
              title: 'Open recon',
              dataIndex: 'open_recon_gaps',
              width: 110,
              render: (v) => (v > 0 ? <Tag color="gold">{v}</Tag> : (v ?? 0)),
            },
            {
              title: 'Status',
              dataIndex: 'operational_status',
              width: 130,
              render: (v) => (v ? <Tag>{v}</Tag> : '—'),
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvFuelRegisterPage;
